import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";
import { ArrowRight } from "react-feather";

import icon, { IIconName } from "@/utils/icon.helper";
import HtmlText from "@/sections/common/HtmlText";
import { IButton } from "@/api/dtos/shared-component.type";

// ----------------------------------------------------------------------------
// SolutionCard v5 — Bento with animated hover.
//
// New in v5:
//   - Animated hover background: each card has an absolute-positioned
//     gradient overlay that fades in over 500ms and adds a deeper tint
//     matching the card's accent. Creates a "wash" effect — the card
//     feels alive when you mouseover without the bg color literally
//     changing (which can be jarring).
//   - Subtle lift on hover (translateY(-2px)) + shadow that matches
//     the accent color
//   - Icon scales slightly + brightens on card hover
//   - Heading shifts toward the accent color on hover (not just at
//     rest)
//
// Card content uses relative + z-10 so it sits above the hover overlay.
// ----------------------------------------------------------------------------

export type ISolutionList = {
  heading: string;
  link: string;
};

export type ISolution = {
  icon?: IIconName;
  heading?: string;
  description?: ReactNode;
  list?: IButton[];
};

export type ISolutionAccent =
  | "hero"
  | "primary"
  | "secondary"
  | "yellow"
  | "white";

export type ISolutionCardProps = HTMLAttributes<HTMLDivElement> &
  ISolution & {
    accent?: ISolutionAccent;
  };

const ACCENT_STYLES: Record<
  ISolutionAccent,
  {
    card: string;
    /** Gradient overlay shown on hover — adds a deeper tint that matches the card */
    hoverOverlay: string;
    /** Shadow color on hover */
    hoverShadow: string;
    iconWrap: string;
    iconClass: string;
    heading: string;
    headingHover: string;
    body: string;
    divider: string;
    linkBase: string;
    linkArrow: string;
  }
> = {
  hero: {
    card: "bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white border-transparent",
    hoverOverlay:
      "bg-gradient-to-tr from-primary-800/60 via-secondary-900/40 to-yellow-400/20",
    hoverShadow: "hover:shadow-2xl hover:shadow-primary-900/40",
    iconWrap: "bg-white/15 group-hover:bg-white/25",
    iconClass: "brightness-0 invert",
    heading: "text-white",
    headingHover: "",
    body: "prose-p:text-white/80",
    divider: "border-white/20",
    linkBase: "text-white/90 hover:text-yellow-300",
    linkArrow: "text-white/50 group-hover/item:text-yellow-300",
  },
  primary: {
    card: "bg-primary-50 border-primary-100",
    hoverOverlay:
      "bg-gradient-to-br from-primary-100/80 via-primary-50 to-primary-200/40",
    hoverShadow: "hover:shadow-xl hover:shadow-primary-900/15",
    iconWrap: "bg-white group-hover:bg-primary-100",
    iconClass: "",
    heading: "text-black-900",
    headingHover: "group-hover:text-primary-900",
    body: "prose-p:text-black-700",
    divider: "border-primary-100",
    linkBase: "text-black-800 hover:text-primary-900",
    linkArrow: "text-primary-300 group-hover/item:text-primary-900",
  },
  secondary: {
    card: "bg-secondary-50 border-secondary-100",
    hoverOverlay:
      "bg-gradient-to-br from-secondary-100/80 via-secondary-50 to-secondary-200/40",
    hoverShadow: "hover:shadow-xl hover:shadow-secondary-900/15",
    iconWrap: "bg-white group-hover:bg-secondary-100",
    iconClass: "",
    heading: "text-black-900",
    headingHover: "group-hover:text-secondary-900",
    body: "prose-p:text-black-700",
    divider: "border-secondary-100",
    linkBase: "text-black-800 hover:text-secondary-900",
    linkArrow: "text-secondary-300 group-hover/item:text-secondary-900",
  },
  yellow: {
    card: "bg-yellow-50 border-yellow-100",
    hoverOverlay:
      "bg-gradient-to-br from-yellow-100/80 via-yellow-50 to-yellow-200/40",
    hoverShadow: "hover:shadow-xl hover:shadow-yellow-400/20",
    iconWrap: "bg-white group-hover:bg-yellow-100",
    iconClass: "",
    heading: "text-black-900",
    headingHover: "group-hover:text-yellow-700",
    body: "prose-p:text-black-700",
    divider: "border-yellow-200",
    linkBase: "text-black-800 hover:text-yellow-700",
    linkArrow: "text-yellow-300 group-hover/item:text-yellow-700",
  },
  white: {
    card: "bg-white border-black-100",
    hoverOverlay:
      "bg-gradient-to-br from-primary-50 via-white to-primary-100/40",
    hoverShadow: "hover:shadow-xl hover:shadow-primary-900/10",
    iconWrap: "bg-primary-50 group-hover:bg-primary-100",
    iconClass: "",
    heading: "text-black-900",
    headingHover: "group-hover:text-primary-900",
    body: "prose-p:text-black-700",
    divider: "border-black-100",
    linkBase: "text-black-800 hover:text-primary-900",
    linkArrow: "text-black-300 group-hover/item:text-primary-900",
  },
};

const SolutionCard: FC<ISolutionCardProps> = (props) => {
  const {
    icon: cardIcon,
    heading,
    description,
    list,
    accent = "white",
    className,
    ...other
  } = props;

  const v = ACCENT_STYLES[accent];
  const isHero = accent === "hero";

  return (
    <div
      className={clsx(
        "group relative flex h-full flex-col overflow-hidden rounded-2xl border p-7 transition-all duration-500 ease-out hover:-translate-y-1 md:p-8 lg:p-9",
        v.card,
        v.hoverShadow,
        className,
      )}
      {...other}
    >
      {/* Animated hover overlay — gradient that fades in over 500ms on
          card hover. Sits behind the content (z-0); content is z-10. */}
      <span
        aria-hidden="true"
        className={clsx(
          "pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100",
          v.hoverOverlay,
        )}
      />

      {/* All real content sits above the overlay */}
      <div className="relative z-10 flex h-full flex-col">
        {cardIcon && (
          <div
            className={clsx(
              "mb-6 flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-110 md:h-14 md:w-14",
              v.iconWrap,
            )}
          >
            <Image
              src={icon(cardIcon)}
              alt={cardIcon}
              className={clsx(
                "h-6 w-6 md:h-7 md:w-7",
                v.iconClass,
              )}
              width={32}
              height={32}
            />
          </div>
        )}

        {heading && (
          <h3
            className={clsx(
              "mb-3 font-bricolage-grotesque font-medium leading-tight transition-colors duration-300",
              v.heading,
              v.headingHover,
              // Fluid heading sizes — hero is bigger across all viewports
              // via a wider clamp range. No breakpoint jumps.
              isHero
                ? "text-[clamp(1.5rem,2.5vw+0.75rem,2.25rem)]" // 24px → 36px
                : "text-[clamp(1.25rem,1vw+0.875rem,1.5rem)]",  // 20px → 24px
            )}
          >
            {heading}
          </h3>
        )}

        {description && (
          <div className="mb-6 flex-1">
            <HtmlText
              className={clsx(
                // Fluid body text — hero gets a wider range.
                isHero
                  ? "prose-p:text-[clamp(0.9375rem,0.6vw+0.8rem,1.125rem)]" // 15-18px
                  : "prose-p:text-[clamp(0.875rem,0.4vw+0.75rem,1rem)]",    // 14-16px
                // Responsive weight — normal on mobile / tablet (font-extralight
                // at 14px reads as washed-out on phones), light on lg+
                // where the bento cards have plenty of room to breathe.
                "prose-p:font-light lg:prose-p:font-extralight",
                // Removed line-clamp — per user feedback, every card's
                // description should read end-to-end without truncation.
                // The bento layout has `flex-1` on this wrapper so cells
                // self-equalize to the tallest description in the row.
                "prose-p:leading-relaxed",
                v.body,
              )}
            >
              {description}
            </HtmlText>
          </div>
        )}

        {list && list.length > 0 && (
          <ul
            className={clsx(
              "space-y-2 border-t pt-5 transition-colors duration-300 md:space-y-2.5",
              v.divider,
            )}
          >
            {list.map((item, idx) => (
              <li key={idx} className="list-none">
                <Link
                  id={item.btn_id}
                  href={item.href ?? ""}
                  target={item.target}
                  className={clsx(
                    // Fluid link size: 14px → 16px
                    "group/item flex items-center justify-between gap-3 text-[clamp(0.875rem,0.4vw+0.75rem,1rem)] font-normal transition-colors",
                    v.linkBase,
                  )}
                >
                  <span>{item.title}</span>
                  <ArrowRight
                    size={16}
                    className={clsx(
                      "flex-shrink-0 transition-all group-hover/item:translate-x-1",
                      v.linkArrow,
                    )}
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SolutionCard;
